/**
 * SightImageGrid — vibe-designer 1.178.0 Phase 5γ.2 + 1.183.0 Phase 6.4-B.
 *
 * Per-service viz on the SIGHT detail panel. Calls
 * GET /api/knowledge/services/sight/recent (metadata-only — R1 C2: no
 * 160MB data URIs in the bulk response) → renders a grid of cards. Each
 * card with `has_image=true` lazy-fetches its full data URI via
 * GET /api/knowledge/services/sight/items/{id}/image on first paint.
 *
 * Phase 6.4-B (2026-05-22, user explicit ask):
 *   1. Text-only cards (`has_image=false`) render a multi-line content
 *      excerpt INSIDE the card body instead of a generic FileText icon
 *      so docs become readable in the grid.
 *   2. Default grid is `grid-cols-6` (was `grid-cols-4`) — smaller cards
 *      out of the box.
 *   3. Scale slider in the header (0.5x–2x, step 0.1x, default 1x)
 *      drives the column count. localStorage-persisted at key
 *      `vibe_designer_sight_card_scale` so user preference survives reload.
 *
 * Empty-state: "No SIGHT vectors yet. Upload PNG/JPEG/GIF via Knowledge →
 * Ingest content."
 */
import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { fetchSightItemImage, type SightRecentItem } from '@/services/knowledgeApi';
import { SightCardZoomModal } from './SightCardZoomModal';

const SCALE_STORAGE_KEY = 'vibe_designer_sight_card_scale';
const SCALE_DEFAULT = 1.0;
const SCALE_MIN = 0.5;
const SCALE_MAX = 2.0;
const SCALE_STEP = 0.1;
// Phase 7.X (2026-05-26) flicker-fix anchor: at scale=1.0 cards target
// ~230px wide (matches prior `grid-cols-6` default on a ~1400px container).
// Higher scale → smaller min-width → more columns; lower scale → larger
// min-width → fewer columns. CSS auto-fill handles the column count
// continuously so the slider doesn't jump grid-template boundaries.
const SCALE_BASE_PX = 230;

function readInitialScale(): number {
  try {
    const raw = localStorage.getItem(SCALE_STORAGE_KEY);
    if (!raw) return SCALE_DEFAULT;
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return SCALE_DEFAULT;
    return Math.min(SCALE_MAX, Math.max(SCALE_MIN, n));
  } catch {
    return SCALE_DEFAULT;
  }
}

/**
 * Continuous min-card-width formula for the auto-fill grid. Larger scale
 * → smaller min-width → more columns; smaller scale → larger min-width
 * → fewer columns. Replaces the prior discrete `grid-cols-N` step
 * function which caused visible re-flow flicker at threshold boundaries.
 */
function minCardWidthPx(scale: number): number {
  return Math.round(SCALE_BASE_PX / scale);
}

export function SightImageGrid() {
  const data = useKnowledgeStore((s) => s.sightRecent);
  const isFetching = useKnowledgeStore((s) => s.isFetchingSightRecent);
  const error = useKnowledgeStore((s) => s.sightRecentError);
  const fetchSightRecent = useKnowledgeStore((s) => s.fetchSightRecent);
  // 1.191.0 Phase 7.6.1 — re-fetch on SSE ingest event so the grid surfaces
  // newly-uploaded images without a manual Refresh click.
  const lastIngestAt = useKnowledgeStore((s) => s.lastIngestAt);
  const [scale, setScale] = useState<number>(readInitialScale);
  // Phase 7.X (2026-05-26) — click-to-zoom. Lifted state so only ONE modal
  // is mounted at a time (mirrors VoiceHistoryLog's expandedToolIdx pattern).
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  useEffect(() => {
    void fetchSightRecent();
  }, [fetchSightRecent, lastIngestAt]);

  useEffect(() => {
    try {
      localStorage.setItem(SCALE_STORAGE_KEY, String(scale));
    } catch {
      // best-effort persistence; in-memory state stays correct
    }
  }, [scale]);

  return (
    <section className="rounded border border-vibe-border bg-vibe-bg-secondary p-3 mb-4">
      <header className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary flex items-center gap-1.5">
          <ImageIcon className="w-3 h-3" />
          Recent SIGHT vectors
          {data && data.items.length > 0 && (
            <span className="ml-2 text-vibe-text-secondary tabular-nums">
              {data.total} item{data.total === 1 ? '' : 's'}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-[10px] text-vibe-text-secondary">
            <span>Card size</span>
            <input
              data-testid="sight-image-grid-scale-slider"
              type="range"
              min={SCALE_MIN}
              max={SCALE_MAX}
              step={SCALE_STEP}
              value={scale}
              onChange={(e) => setScale(parseFloat(e.target.value))}
              className="w-20 accent-vibe-accent"
              title={`${scale.toFixed(1)}x`}
            />
          </label>
          <button
            data-testid="sight-image-grid-refresh"
            type="button"
            onClick={() => void fetchSightRecent()}
            disabled={isFetching}
            className="px-2 py-1 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50 flex items-center gap-1"
          >
            {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {error && (
        <div
          data-testid="sight-image-grid-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
        >
          {error.message}
        </div>
      )}

      {!error && data && data.error && (
        <div
          data-testid="sight-image-grid-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
        >
          {data.error}
        </div>
      )}

      {!error && data && !data.error && data.items.length === 0 && (
        <div
          data-testid="sight-image-grid-empty"
          className="text-xs text-vibe-text-secondary py-2"
        >
          No SIGHT vectors yet. Upload PNG/JPEG/GIF via Knowledge → Ingest content.
        </div>
      )}

      {!error && data && !data.error && data.items.length > 0 && (
        <div
          data-testid="sight-image-grid"
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidthPx(scale)}px, 1fr))`,
          }}
        >
          {data.items.map((item) => (
            <SightCard
              key={item.id}
              item={item}
              expanded={expandedItemId === item.id}
              onToggle={() =>
                setExpandedItemId((cur) => (cur === item.id ? null : item.id))
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface SightCardProps {
  item: SightRecentItem;
  expanded: boolean;
  onToggle: () => void;
}

function SightCard({ item, expanded, onToggle }: SightCardProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    // R1 C2 lazy-load: only fetch the full data URI for rows with has_image=true.
    // Text-only rows skip the per-item fetch entirely.
    if (!item.has_image) return;
    let cancelled = false;
    setLoading(true);
    void fetchSightItemImage(item.id)
      .then((r) => {
        if (cancelled) return;
        if (r.image_url) setImageUrl(r.image_url);
        else setLoadError(r.error || 'no image_url');
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'fetch failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id, item.has_image]);

  // Phase 6.4-B BUG-SIGHT-2 LOCK — text-only cards show a content excerpt
  // (first ~150 chars) inside the card body so docs are actually readable
  // in the grid. The fade-out gradient masks truncated content visually.
  const isTextOnly = !item.has_image;
  const excerpt = isTextOnly ? (item.content || '').slice(0, 200) : '';

  return (
    <>
    <div
      data-testid={`sight-card-${item.id}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      aria-label={`open zoom view for ${item.source_file || item.id}`}
      className="rounded border border-vibe-border bg-vibe-bg overflow-hidden flex flex-col cursor-pointer hover:border-vibe-accent focus:outline-none focus:border-vibe-accent"
    >
      <div className="aspect-square bg-vibe-bg-tertiary flex items-center justify-center relative overflow-hidden">
        {item.has_image && imageUrl && (
          <img
            src={imageUrl}
            alt={item.source_file || 'SIGHT image'}
            className="w-full h-full object-cover"
            data-testid={`sight-card-img-${item.id}`}
          />
        )}
        {item.has_image && loading && !imageUrl && (
          <Loader2 className="w-6 h-6 animate-spin text-vibe-text-secondary" />
        )}
        {item.has_image && loadError && (
          <span className="text-[10px] text-vibe-error px-1 text-center">
            image fetch failed
          </span>
        )}
        {isTextOnly && (
          <>
            <div
              data-testid={`sight-card-content-${item.id}`}
              className="absolute inset-0 p-2 text-[9px] leading-snug text-vibe-text-secondary overflow-hidden font-mono whitespace-pre-wrap"
            >
              {excerpt}
            </div>
            <div
              aria-hidden
              className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-vibe-bg-tertiary to-transparent pointer-events-none"
            />
          </>
        )}
      </div>
      <div className="p-1.5 text-[10px] text-vibe-text-secondary truncate"
           title={item.source_file || item.content}>
        {item.source_file || item.content.slice(0, 40)}
      </div>
    </div>
    {expanded && (
      <SightCardZoomModal
        item={item}
        imageUrl={imageUrl}
        onClose={onToggle}
      />
    )}
    </>
  );
}
